// src/components/pages/MyOrders.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getColors } from '../../styles/colors';
import { 
  FaHistory, FaCalendarAlt, FaClock, FaArrowLeft,
  FaLeaf, FaFish, FaTrash, FaShoppingCart,
  FaEye, FaSpinner, FaCheck, FaTimes,
  FaRupeeSign, FaEdit, FaPlus, FaCalendarDay,
  FaInfoCircle, FaTimesCircle, FaList, FaArrowRight,
  FaMapMarkerAlt, FaLock
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getSubscriptionListAsync } from '../../redux/slices/subscriptionSlice';
import { cancelOrderAsync } from '../../redux/slices/cartSlice';
import {
  fetchMealOffsAsync,
  markMealOffAsync,
  removeMealOffAsync,
  selectMealOffsForSubscription,
  selectMealOffLoading,
  selectMealOffError,
  selectIsMealSkipped,
  selectSkippedMealsForDate,
  selectIsDateFullyLocked,
  selectAvailableMealTimes,
} from '../../redux/slices/mealOffSlice';
import apiService from '../../api/authApi';

const MyOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);
  
  const { user } = useSelector((state) => state.auth || { user: null });
  
  const [subscriptions, setSubscriptions] = useState([]);
  const [cartOrders, setCartOrders] = useState([]);
  const [customOrders, setCustomOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [products, setProducts] = useState([]);
  const [productMap, setProductMap] = useState({});
  const [mealOffPrices, setMealOffPrices] = useState({});
  const [modalKey, setModalKey] = useState(0);

  // Meal Off states
  const [showMealOffModal, setShowMealOffModal] = useState(false);
  const [mealOffData, setMealOffData] = useState({
    date: '',
    meal_time: 'Breakfast',
    meal_type: 'Veg'
  });
  const [mealOffMessage, setMealOffMessage] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [showMealOffList, setShowMealOffList] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const mealTimes = ['Breakfast', 'Lunch', 'Dinner'];
  const mealTypes = ['Veg', 'Non-Veg'];

  // Redux selectors for meal offs - moved to component level
  const mealOffs = useSelector(selectMealOffsForSubscription(currentSubscription?.id));
  const mealOffLoading = useSelector(selectMealOffLoading);
  const mealOffError = useSelector(selectMealOffError);

  // Helper functions using useMemo to create selectors
  const getSkippedMealsForDate = useCallback((dateStr) => {
    if (!currentSubscription?.id || !dateStr) return [];
    return mealOffs.filter(off => {
      const offDate = off.date || off.Date;
      return offDate === dateStr;
    });
  }, [mealOffs, currentSubscription?.id]);

  const getIsDateFullyLocked = useCallback((dateStr) => {
    if (!currentSubscription?.id || !dateStr) return false;
    const skippedForDate = getSkippedMealsForDate(dateStr);
    const mealTimesSkipped = skippedForDate.map(m => m.meal_time || m.MealTime);
    const allMeals = ['Breakfast', 'Lunch', 'Dinner'];
    const allSkipped = allMeals.every(meal => mealTimesSkipped.includes(meal));
    return allSkipped && skippedForDate.length >= 3;
  }, [getSkippedMealsForDate]);

  const getAvailableMealTimes = useCallback((dateStr) => {
    if (!currentSubscription?.id || !dateStr) return mealTimes;
    const skippedForDate = getSkippedMealsForDate(dateStr);
    const skippedMealTimes = skippedForDate.map(m => m.meal_time || m.MealTime);
    return mealTimes.filter(time => !skippedMealTimes.includes(time));
  }, [getSkippedMealsForDate, mealTimes]);

  const isMealSkipped = useCallback((dateStr, mealTime, mealType) => {
    if (!currentSubscription?.id || !dateStr) return false;
    return mealOffs.some(off => {
      const offDate = off.date || off.Date;
      const offMealTime = off.meal_time || off.mealTime || off.MealTime;
      const offMealType = off.meal_type || off.mealType || off.MealType;
      
      let typeMatches = false;
      if (Array.isArray(offMealType)) {
        typeMatches = offMealType.some(t => t.toLowerCase() === mealType?.toLowerCase());
      } else if (typeof offMealType === 'string') {
        typeMatches = offMealType.toLowerCase() === mealType?.toLowerCase();
      }
      
      return offDate === dateStr && offMealTime === mealTime && typeMatches;
    });
  }, [mealOffs, currentSubscription?.id]);

  const DEFAULT_IMAGE = 'https://ui-avatars.com/api/?name=Product&background=FF6B35&color=fff&size=128';

  // Helper to get token
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Get product details for cart items
  const getCartItemDetails = useCallback((productId) => {
    if (!productId) return { name: 'Product', image: DEFAULT_IMAGE, type: 'Veg', price: 0 };
    const product = productMap[parseInt(productId)];
    return {
      name: product?.product_name || product?.name || `Product #${productId}`,
      image: product?.image_url || `https://ui-avatars.com/api/?name=Product&background=FF6B35&color=fff&size=128`,
      type: product?.type || 'Veg',
      price: product?.price || 0
    };
  }, [productMap]);

  // FETCH PRODUCTS
  const fetchProducts = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        setProducts([]);
        setProductMap({});
        return;
      }
      
      const response = await apiService.getProducts();
      
      if (response?.status && response?.data) {
        setProducts(response.data);
        
        const map = {};
        response.data.forEach(product => {
          let imageUrl = null;
          if (product.prod_image) {
            imageUrl = `https://admin.thefoodies.in/public/uploads/products/${product.prod_image}`;
          }
          
          map[product.id] = {
            ...product,
            image_url: imageUrl,
            name: product.product_name || `Product #${product.id}`,
            type: product.type || 'Veg',
            price: parseFloat(product.price) || 0
          };
        });
        
        setProductMap(map);
        console.log('✅ Product map loaded:', Object.keys(map).length, 'products');
      }
    } catch (error) {
      console.error('❌ Failed to fetch products:', error);
      setProducts([]);
      setProductMap({});
    }
  }, []);

  const getProductImage = (productId) => {
    if (!productId) return DEFAULT_IMAGE;
    const product = productMap[parseInt(productId)];
    if (product && product.image_url) {
      return product.image_url;
    }
    const productName = getProductName(productId);
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(productName)}&background=FF6B35&color=fff&size=128`;
  };

  const getProductName = (productId) => {
    if (!productId) return `Product #${productId}`;
    const product = productMap[parseInt(productId)];
    return product?.product_name || product?.name || `Product #${productId}`;
  };

  // ========== FETCH SUBSCRIPTION DETAILS ==========
  const fetchSubscriptionDetails = useCallback(async (subscriptionId) => {
    try {
      const token = getToken();
      if (!token) {
        return null;
      }
      
      console.log('🔍 Fetching subscription details:', subscriptionId);
      const response = await apiService.getSubscriptionDetails(subscriptionId);
      
      if (response?.status && response?.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error(`❌ Failed to fetch subscription ${subscriptionId}:`, error);
      return null;
    }
  }, []);

  // ========== FETCH ALL ORDERS ==========
  const fetchAllOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await fetchProducts();

      // 1. Fetch subscription list
      try {
        const subResult = await dispatch(getSubscriptionListAsync()).unwrap();
        console.log('📦 Subscription List API Response:', subResult);
        
        if (subResult?.data) {
          const enrichedSubscriptions = [];
          
          for (const sub of subResult.data) {
            const projectedTotal = parseFloat(sub.projected_total) || 0;
            const depositAmount = parseFloat(sub.deposit_amount) || (projectedTotal * 0.5);
            
            let items = [];
            let breakdown = [];
            let mealOffs = [];
            let totalMeals = 0;
            const mealsByType = {};
            let currentTotalAfterOffs = parseFloat(sub.current_total_after_offs) || projectedTotal;
            let outstandingAmount = parseFloat(sub.outstanding_amount) || 0;
            
            try {
              const details = await fetchSubscriptionDetails(sub.id);
              if (details) {
                items = details.items || [];
                breakdown = details.breakdown || [];
                mealOffs = details.meal_offs || [];
                currentTotalAfterOffs = parseFloat(details.current_total_after_offs) || currentTotalAfterOffs;
                outstandingAmount = parseFloat(details.outstanding_amount) || outstandingAmount;
                
                if (items && Array.isArray(items)) {
                  items.forEach(item => {
                    const quantity = parseInt(item.quantity) || 0;
                    totalMeals += quantity;
                    
                    const mealTime = item.meal_time || 'Meal';
                    const mealType = item.meal_type || 'Veg';
                    const key = `${mealTime} (${mealType})`;
                    mealsByType[key] = (mealsByType[key] || 0) + quantity;
                  });
                }
              }
            } catch (detailError) {
              console.error(`❌ Failed to fetch details for sub ${sub.id}:`, detailError);
            }
            
            mealOffs = Array.isArray(mealOffs) ? mealOffs : [];
            
            console.log(`📋 Subscription #${sub.id} has ${mealOffs.length} meal offs:`, mealOffs);
            
            const discountPercent = projectedTotal > 0 ? Math.round(((projectedTotal - currentTotalAfterOffs) / projectedTotal) * 100) : 0;
            
            enrichedSubscriptions.push({
              ...sub,
              items: items,
              breakdown: breakdown,
              mealOffs: mealOffs,
              current_total_after_offs: currentTotalAfterOffs,
              projected_total: projectedTotal,
              deposit_amount: depositAmount,
              outstanding_amount: outstandingAmount,
              total_meals: totalMeals,
              meals_by_type: mealsByType,
              delivery_address: sub.delivery_address || 'N/A',
              discount_percent: discountPercent
            });
          }
          
          setSubscriptions(enrichedSubscriptions);
        }
      } catch (subError) {
        console.error('❌ Failed to fetch subscriptions:', subError);
      }

      // 2. Fetch cart order history
      try {
        console.log('🔄 Fetching cart order history...');
        const response = await apiService.getOrderHistory();
        console.log('📦 Cart order response:', response);
        
        if (response?.status && response?.data) {
          const ordersData = Array.isArray(response.data) ? response.data : [];
          console.log('📦 Cart orders data:', ordersData);
          
          const orders = ordersData.map(order => ({
            order_id: order.order_id,
            total: parseFloat(order.total) || 0,
            status: order.status || 'pending',
            payment: order.payment || 'pending',
            created_at: order.created_at,
            items: (order.items || []).map(item => ({
              id: item.id,
              order_id: item.order_id,
              product_id: parseInt(item.product_id) || 0,
              item_type: item.item_type || 'product',
              quantity: parseInt(item.quantity) || 1,
              price: parseFloat(item.price) || 0,
              subtotal: parseFloat(item.subtotal) || 0
            })),
            customer_address: order.customer_address || order.address,
            customer_phone: order.customer_phone || order.phone,
            customer_name: order.customer_name || order.name,
            order_number: order.order_number || order.order_no
          }));
          console.log('✅ Processed cart orders:', orders);
          setCartOrders(orders);
        } else {
          console.warn('⚠️ No cart orders found or invalid response');
          setCartOrders([]);
        }
      } catch (cartError) {
        console.error('❌ Failed to fetch cart orders:', cartError);
        setCartOrders([]);
      }

      // 3. Fetch custom orders from localStorage
      try {
        const savedOrders = localStorage.getItem('customOrders');
        if (savedOrders) {
          const parsed = JSON.parse(savedOrders);
          const activeOrders = Array.isArray(parsed) ? parsed.filter(order => 
            order.status?.toLowerCase() !== 'cancelled'
          ) : [];
          setCustomOrders(activeOrders);
        } else {
          setCustomOrders([]);
        }
      } catch (localError) {
        console.error('❌ Failed to fetch custom orders:', localError);
        setCustomOrders([]);
      }
      
    } catch (error) {
      console.error('❌ Failed to fetch orders:', error);
      setError('Failed to load orders. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, fetchProducts, fetchSubscriptionDetails]);

  useEffect(() => {
    fetchAllOrders();
  }, [fetchAllOrders, refreshKey]);

  // ========== CANCEL CART ORDER ==========
  const handleCancelCartOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    
    try {
      setIsLoading(true);
      const result = await dispatch(cancelOrderAsync(orderId)).unwrap();
      if (result?.status) {
        alert('✅ Order cancelled successfully!');
        setRefreshKey(prev => prev + 1);
      } else {
        alert('❌ Failed to cancel order: ' + (result?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Failed to cancel order:', error);
      alert('❌ Failed to cancel order. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ========== CANCEL CUSTOM DATE ORDER ==========
  const handleCancelCustomOrder = async (orderId) => {
    const token = getToken();
    if (!token) {
      alert('❌ Please login again. Token not found.');
      return;
    }

    const order = customOrders.find(o => (o.order_id || o.id) === orderId);
    if (order?.status?.toLowerCase() === 'cancelled') {
      alert('ℹ️ This order has already been cancelled.');
      return;
    }

    if (!window.confirm('Are you sure you want to cancel this custom order?')) return;
    
    try {
      setIsLoading(true);
      console.log('🔄 Cancelling custom order:', orderId);
      
      const response = await apiService.cancelCustomDateOrder(orderId);
      
      if (response?.status) {
        alert('✅ Custom order cancelled successfully!');
        
        setCustomOrders(prev => prev.filter(order => 
          (order.order_id || order.id) !== orderId
        ));
        
        setRefreshKey(prev => prev + 1);
        
        if (selectedOrder && (selectedOrder.order_id === orderId || selectedOrder.id === orderId)) {
          setShowOrderDetail(false);
          setSelectedOrder(null);
        }
        
        await fetchAllOrders();
        
      } else {
        alert('❌ Failed to cancel order: ' + (response?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Failed to cancel custom order:', error);
      
      if (error.response?.status === 404) {
        alert('❌ Order not found. It may have already been cancelled.');
      } else if (error.response?.status === 401) {
        alert('❌ Please login again to cancel this order.');
      } else if (error.response?.status === 400) {
        alert('❌ Order cannot be cancelled. It may already be processed or completed.');
      } else {
        alert('❌ Failed to cancel custom order. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ========== MEAL OFF FUNCTIONS ==========
  
  const handleOpenMealOffModal = useCallback(async (subscription) => {
    const token = getToken();
    if (!token) {
      setMealOffMessage({ 
        type: 'error', 
        text: '❌ Please login again. Token not found.'
      });
      setTimeout(() => setMealOffMessage(null), 3000);
      return;
    }

    if (subscription.status?.toLowerCase() !== 'active' && subscription.status?.toLowerCase() !== 'pending_payment') {
      setMealOffMessage({ 
        type: 'error', 
        text: '❌ This subscription is not active. You can only modify active subscriptions.'
      });
      setTimeout(() => setMealOffMessage(null), 3000);
      return;
    }
    
    console.log('🔑 Opening meal off modal for subscription:', subscription.id);
    
    setMealOffData({
      date: '',
      meal_time: 'Breakfast',
      meal_type: 'Veg'
    });
    setMealOffMessage(null);
    setCurrentMonth(new Date());
    setShowMealOffList(false);
    setMealOffPrices({});
    
    try {
      console.log('🔄 Fetching fresh meal offs from API for subscription:', subscription.id);
      
      // Use Redux thunk to fetch meal offs
      const result = await dispatch(fetchMealOffsAsync(subscription.id)).unwrap();
      console.log('✅ Fresh meal offs loaded:', result.mealOffs.length, 'items');
      
      const details = await fetchSubscriptionDetails(subscription.id);
      if (details) {
        console.log('✅ Fresh subscription details loaded');
        
        if (details.breakdown && Array.isArray(details.breakdown)) {
          const prices = {};
          details.breakdown.forEach(meal => {
            const key = `${meal.date}-${meal.meal_time}-${meal.meal_type || 'Veg'}`;
            prices[key] = parseFloat(meal.subtotal) || 0;
          });
          setMealOffPrices(prices);
        }
        
        const updatedSubscription = {
          ...subscription,
          mealOffs: result.mealOffs || [],
          current_total_after_offs: parseFloat(details.current_total_after_offs) || subscription.current_total_after_offs,
          outstanding_amount: parseFloat(details.outstanding_amount) || subscription.outstanding_amount,
          breakdown: details.breakdown || subscription.breakdown,
          items: details.items || subscription.items,
          total_meals: details.total_meals || subscription.total_meals
        };
        
        setCurrentSubscription(updatedSubscription);
        
        setSubscriptions(prevSubs => 
          prevSubs.map(sub => 
            sub.id === subscription.id ? updatedSubscription : sub
          )
        );
      } else {
        const updatedSubscription = {
          ...subscription,
          mealOffs: result.mealOffs || []
        };
        setCurrentSubscription(updatedSubscription);
      }
      
    } catch (error) {
      console.error('❌ Failed to refresh meal offs:', error);
      const existingMealOffs = subscription.mealOffs || [];
      // Still set the subscription with existing data
      setCurrentSubscription(subscription);
      
      setMealOffMessage({ 
        type: 'error', 
        text: '⚠️ Could not fetch latest data. Showing cached data.'
      });
      setTimeout(() => setMealOffMessage(null), 3000);
    }
    
    setModalKey(prev => prev + 1);
    setShowMealOffModal(true);
  }, [dispatch, fetchSubscriptionDetails]);

  const handleMarkMealOff = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setMealOffMessage({ 
        type: 'error', 
        text: '❌ Please login again. Token not found.'
      });
      setTimeout(() => setMealOffMessage(null), 3000);
      return;
    }

    if (!mealOffData.date) {
      setMealOffMessage({ type: 'error', text: 'Please select a date.' });
      return;
    }

    if (!mealOffData.meal_time) {
      setMealOffMessage({ type: 'error', text: 'Please select a meal time.' });
      return;
    }

    if (!mealOffData.meal_type) {
      setMealOffMessage({ type: 'error', text: 'Please select a meal type.' });
      return;
    }

    // Check if date is fully locked using the helper function
    const isFullyLocked = getIsDateFullyLocked(mealOffData.date);
    if (isFullyLocked) {
      setMealOffMessage({ 
        type: 'error', 
        text: '❌ This date is fully locked. All meals have been skipped.'
      });
      setTimeout(() => setMealOffMessage(null), 3000);
      return;
    }

    // Check if meal is already skipped using the helper function
    const alreadySkipped = isMealSkipped(mealOffData.date, mealOffData.meal_time, mealOffData.meal_type);
    
    if (alreadySkipped) {
      setMealOffMessage({ type: 'error', text: 'This meal is already skipped!' });
      setTimeout(() => setMealOffMessage(null), 3000);
      return;
    }

    setMealOffMessage(null);

    try {
      const subscriptionId = currentSubscription.id;
      console.log('🔄 Marking meal off for subscription:', subscriptionId);
      console.log('📤 Data:', {
        date: mealOffData.date,
        meal_time: mealOffData.meal_time,
        meal_type: mealOffData.meal_type
      });

      // Use Redux thunk to mark meal off
      const result = await dispatch(markMealOffAsync({
        subscriptionId,
        date: mealOffData.date,
        meal_time: mealOffData.meal_time,
        meal_type: mealOffData.meal_type
      })).unwrap();
      
      // Get price for this meal
      const priceKey = `${mealOffData.date}-${mealOffData.meal_time}-${mealOffData.meal_type}`;
      const mealPrice = mealOffPrices[priceKey] || 0;
      const outstanding = result.data?.outstanding_amount || 0;
      
      setMealOffMessage({ 
        type: 'success', 
        text: `✅ Meal skipped! ₹${mealPrice.toFixed(2)} deducted. New total: ₹${outstanding.toFixed(2)}`
      });
      
      // Update subscription in the list with fresh data
      if (result.data) {
        const updatedSubscription = {
          ...currentSubscription,
          mealOffs: result.data.meal_offs || [],
          current_total_after_offs: parseFloat(result.data.current_total_after_offs) || currentSubscription.current_total_after_offs,
          outstanding_amount: parseFloat(result.data.outstanding_amount) || currentSubscription.outstanding_amount
        };
        setCurrentSubscription(updatedSubscription);
        
        setSubscriptions(prevSubs => 
          prevSubs.map(sub => 
            sub.id === subscriptionId ? updatedSubscription : sub
          )
        );
      }
      
      // Reset meal selection
      setMealOffData(prev => ({
        ...prev,
        meal_time: 'Breakfast',
        meal_type: 'Veg'
      }));
      
      // Refresh the main list after a moment
      setTimeout(() => {
        setRefreshKey(prev => prev + 1);
        setModalKey(prev => prev + 1);
      }, 500);
      
      setTimeout(() => {
        setMealOffMessage(null);
      }, 3000);
      
    } catch (error) {
      console.error('❌ Failed to mark meal off:', error);
      setMealOffMessage({ 
        type: 'error', 
        text: error || 'Failed to mark meal off. Please try again.'
      });
    }
  }, [mealOffData, currentSubscription, mealOffPrices, dispatch, getIsDateFullyLocked, isMealSkipped]);

  const handleRemoveMealOff = useCallback(async (date, mealTime, mealType) => {
    const token = getToken();
    if (!token) {
      setMealOffMessage({ 
        type: 'error', 
        text: '❌ Please login again. Token not found.'
      });
      setTimeout(() => setMealOffMessage(null), 3000);
      return;
    }

    if (!window.confirm(`Remove meal off for ${date} - ${mealTime} (${mealType})?`)) return;

    try {
      const subscriptionId = currentSubscription.id;
      console.log('🔄 Removing meal off for subscription:', subscriptionId);
      console.log('📤 Data:', {
        date: date,
        meal_time: mealTime,
        meal_type: mealType
      });
      
      // Use Redux thunk to remove meal off
      const result = await dispatch(removeMealOffAsync({
        subscriptionId,
        date,
        meal_time: mealTime,
        meal_type: mealType
      })).unwrap();
      
      // Update subscription with fresh data
      if (result.data) {
        const updatedSubscription = {
          ...currentSubscription,
          mealOffs: result.data.meal_offs || [],
          current_total_after_offs: parseFloat(result.data.current_total_after_offs) || currentSubscription.current_total_after_offs,
          outstanding_amount: parseFloat(result.data.outstanding_amount) || currentSubscription.outstanding_amount
        };
        setCurrentSubscription(updatedSubscription);
        
        setSubscriptions(prevSubs => 
          prevSubs.map(sub => 
            sub.id === subscriptionId ? updatedSubscription : sub
          )
        );
      }
      
      setMealOffMessage({ 
        type: 'success', 
        text: '✅ Meal off removed successfully!'
      });
      
      // Refresh data after a moment
      setTimeout(() => {
        setRefreshKey(prev => prev + 1);
        setModalKey(prev => prev + 1);
      }, 500);
      
      setTimeout(() => {
        setMealOffMessage(null);
      }, 3000);
    } catch (error) {
      console.error('❌ Failed to remove meal off:', error);
      setMealOffMessage({ 
        type: 'error', 
        text: error || 'Failed to remove meal off. Please try again.'
      });
    }
  }, [currentSubscription, dispatch]);

  // ========== CALENDAR FUNCTIONS ==========
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { daysInMonth, firstDay };
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const getDateStr = (month, day) => {
    const year = month.getFullYear();
    const monthNum = String(month.getMonth() + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${year}-${monthNum}-${dayStr}`;
  };

  const getStatusColor = (status) => {
    if (!status) return '#6B7280';
    const statusLower = status.toLowerCase();
    if (statusLower === 'cancelled') return '#F59E0B';
    switch (statusLower) {
      case 'active':
      case 'completed':
      case 'paid':
      case 'confirmed':
      case 'delivered':
        return '#22C55E';
      case 'pending':
      case 'pending_payment':
      case 'placed':
        return '#F59E0B';
      case 'processing':
        return '#3B82F6';
      default:
        return '#6B7280';
    }
  };

  const getStatusText = (status) => {
    if (!status) return 'Unknown';
    const statusLower = status.toLowerCase();
    if (statusLower === 'cancelled') return 'Cancelled';
    switch (statusLower) {
      case 'pending_payment': return 'Pending Payment';
      case 'pending': return 'Pending';
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      case 'confirmed': return 'Confirmed';
      case 'placed': return 'Placed';
      case 'processing': return 'Processing';
      case 'delivered': return 'Delivered';
      default: return status;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDateFull = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'N/A';
    }
  };

  const getMealIcon = (mealTime) => {
    const icons = {
      'Breakfast': '🌅',
      'Lunch': '☀️',
      'Dinner': '🌙'
    };
    return icons[mealTime] || '🍽️';
  };

  const handleViewOrder = (order, type) => {
    setSelectedOrder({ ...order, orderType: type });
    setShowOrderDetail(true);
  };

  const handleCancelSubscription = async (subscriptionId) => {
    const token = getToken();
    if (!token) {
      alert('❌ Please login again. Token not found.');
      return;
    }

    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    
    try {
      await apiService.cancelSubscription(subscriptionId);
      alert('✅ Subscription cancelled successfully!');
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('❌ Failed to cancel subscription:', error);
      alert('❌ Failed to cancel subscription. Please try again.');
    }
  };

  const getPaymentColor = (payment) => {
    const paymentLower = payment?.toLowerCase() || '';
    if (paymentLower === 'paid' || paymentLower === 'completed') {
      return '#22C55E';
    } else if (paymentLower === 'pending' || paymentLower === 'pending_payment') {
      return '#F59E0B';
    } else if (paymentLower === 'failed') {
      return '#EF4444';
    }
    return '#6B7280';
  };

  const getAllOrders = () => {
    const all = [];
    
    if (cartOrders && cartOrders.length > 0) {
      cartOrders.forEach(order => {
        all.push({
          ...order,
          orderType: 'cart',
          displayName: `Order #${order.order_id}`,
          date: order.created_at,
          total: parseFloat(order.total) || 0,
          status: order.status || 'pending',
          payment: order.payment || 'pending',
          source: 'cart',
          items: order.items || []
        });
      });
    }

    if (subscriptions && subscriptions.length > 0) {
      subscriptions.forEach(sub => {
        const currentTotalAfterOffs = parseFloat(sub.current_total_after_offs) || parseFloat(sub.projected_total) || 0;
        const projectedTotal = parseFloat(sub.projected_total) || 0;
        const depositAmount = parseFloat(sub.deposit_amount) || (projectedTotal * 0.5);
        const outstandingAmount = parseFloat(sub.outstanding_amount) || 0;
        
        const displayTotal = outstandingAmount > 0 ? outstandingAmount : currentTotalAfterOffs;
        
        all.push({
          ...sub,
          orderType: 'subscription',
          displayName: `Monthly Subscription #${sub.id}`,
          date: sub.start_date || sub.created_at,
          total: displayTotal,
          projected_total: projectedTotal,
          current_total_after_offs: currentTotalAfterOffs,
          deposit_amount: depositAmount,
          outstanding_amount: outstandingAmount,
          status: sub.status || 'pending',
          source: 'subscription',
          items: sub.items || [],
          mealOffs: sub.mealOffs || [],
          breakdown: sub.breakdown || [],
          total_meals: sub.total_meals || 0,
          meals_by_type: sub.meals_by_type || {},
          delivery_address: sub.delivery_address || 'N/A',
          discount_percent: sub.discount_percent || 0
        });
      });
    }

    if (customOrders && customOrders.length > 0) {
      customOrders.forEach(order => {
        if (order.status?.toLowerCase() === 'cancelled') return;
        
        all.push({
          ...order,
          orderType: 'custom',
          displayName: `Custom Order #${order.order_id || order.id}`,
          date: order.created_at || order.date,
          total: order.total_amount || order.total || 0,
          status: order.status || 'pending',
          source: 'custom',
          items: order.items || []
        });
      });
    }

    all.sort((a, b) => {
      const dateA = a.date ? new Date(a.date) : new Date(0);
      const dateB = b.date ? new Date(b.date) : new Date(0);
      return dateB - dateA;
    });
    return all;
  };

  const getFilteredOrders = () => {
    const all = getAllOrders();
    if (activeTab === 'all') return all;
    if (activeTab === 'cart') return all.filter(o => o.orderType === 'cart');
    if (activeTab === 'subscription') return all.filter(o => o.orderType === 'subscription');
    if (activeTab === 'custom') return all.filter(o => o.orderType === 'custom');
    return all;
  };

  const filteredOrders = getFilteredOrders();

  // ========== ORDER DETAIL VIEW ==========
  const OrderDetailView = () => {
    if (!selectedOrder) return null;

    const items = selectedOrder.items || [];
    const isCartOrder = selectedOrder.orderType === 'cart';
    const isSubscription = selectedOrder.orderType === 'subscription';
    const isCustomOrder = selectedOrder.orderType === 'custom';
    const mealOffs = selectedOrder.mealOffs || [];
    const totalAmount = parseFloat(selectedOrder.current_total_after_offs || selectedOrder.total || 0);
    const originalTotal = parseFloat(selectedOrder.projected_total || selectedOrder.total || 0);
    const depositAmount = parseFloat(selectedOrder.deposit_amount || 0);
    const outstandingAmount = parseFloat(selectedOrder.outstanding_amount || 0);
    const totalMeals = selectedOrder.total_meals || 0;
    const mealsByType = selectedOrder.meals_by_type || {};

    const canCancelCart = isCartOrder && 
      (selectedOrder.status?.toLowerCase() === 'pending' || 
       selectedOrder.status?.toLowerCase() === 'placed' ||
       selectedOrder.status?.toLowerCase() === 'confirmed') &&
      selectedOrder.status?.toLowerCase() !== 'cancelled';

    const canCancelCustom = isCustomOrder && 
      (selectedOrder.status?.toLowerCase() === 'pending' || 
       selectedOrder.status?.toLowerCase() === 'placed') &&
      selectedOrder.status?.toLowerCase() !== 'cancelled';

    const currentTotal = isSubscription 
      ? (outstandingAmount > 0 ? outstandingAmount : totalAmount)
      : totalAmount;

    const hasDiscount = isSubscription && originalTotal > totalAmount;

    return (
      <div className="space-y-4">
        <button
          onClick={() => { setShowOrderDetail(false); setSelectedOrder(null); }}
          className="flex items-center gap-2 text-sm font-medium transition-all hover:scale-105"
          style={{ color: colors.primary }}
        >
          <FaArrowLeft /> Back to Orders
        </button>

        <div className="rounded-2xl p-6" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="text-2xl font-bold" style={{ color: colors.text }}>
              {isCartOrder ? '🛒 Cart Order' : isSubscription ? '📆 Monthly Subscription' : '📅 Custom Order'}
            </h2>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: getStatusColor(selectedOrder.status) }}>
                {getStatusText(selectedOrder.status)}
              </span>
              {isCartOrder && selectedOrder.payment && (
                <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ 
                  backgroundColor: getPaymentColor(selectedOrder.payment) + '20',
                  color: getPaymentColor(selectedOrder.payment)
                }}>
                  {selectedOrder.payment}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between">
              <span style={{ color: colors.textSecondary }}>Order ID</span>
              <span style={{ color: colors.text }}>#{selectedOrder.order_id || selectedOrder.id || 'N/A'}</span>
            </div>
            
            {isSubscription ? (
              <>
                <div className="flex justify-between">
                  <span style={{ color: colors.textSecondary }}>Start Date</span>
                  <span style={{ color: colors.text }}>{formatDate(selectedOrder.start_date)}</span>
                </div>
                <div className="flex justify-between">
                  <span style={{ color: colors.textSecondary }}>End Date</span>
                  <span style={{ color: colors.text }}>{formatDate(selectedOrder.end_date)}</span>
                </div>
              </>
            ) : (
              <div className="flex justify-between">
                <span style={{ color: colors.textSecondary }}>Date</span>
                <span style={{ color: colors.text }}>{formatDate(selectedOrder.created_at || selectedOrder.date)}</span>
              </div>
            )}
            
            {selectedOrder.delivery_address && selectedOrder.delivery_address !== 'N/A' && (
              <div className="flex justify-between col-span-2">
                <span style={{ color: colors.textSecondary }}>Delivery Address</span>
                <span style={{ color: colors.text }} className="text-right">{selectedOrder.delivery_address}</span>
              </div>
            )}
            
            {isSubscription && (
              <>
                <div className="flex justify-between col-span-2 pt-2 border-t" style={{ borderColor: colors.border }}>
                  <span style={{ color: colors.textSecondary }}>Total Meals</span>
                  <span style={{ color: colors.text }}>{totalMeals} meal{totalMeals !== 1 ? 's' : ''}</span>
                </div>
                
                {Object.keys(mealsByType).length > 0 && (
                  <div className="flex justify-between col-span-2" style={{ borderColor: colors.border }}>
                    <span style={{ color: colors.textSecondary }}>Meal Details</span>
                    <div className="text-right">
                      {Object.entries(mealsByType).map(([key, count], idx) => {
                        const [mealTime, mealType] = key.split(' (');
                        const type = mealType ? mealType.replace(')', '') : 'Veg';
                        return (
                          <div key={idx} className="text-sm flex items-center gap-1 justify-end" style={{ color: colors.text }}>
                            <span>{getMealIcon(mealTime)}</span>
                            <span>{mealTime}</span>
                            <span className="text-[10px] flex items-center gap-0.5" style={{ 
                              color: type === 'Veg' ? '#22C55E' : '#EF4444'
                            }}>
                              {type === 'Veg' ? <FaLeaf className="text-[8px]" /> : <FaFish className="text-[8px]" />}
                              {type}: {count}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <div className="flex justify-between col-span-2" style={{ borderColor: colors.border }}>
                  <span style={{ color: colors.textSecondary }}>Projected Total</span>
                  <span style={{ color: colors.text }}>₹{parseFloat(selectedOrder.projected_total || 0).toFixed(2)}</span>
                </div>
                
                {depositAmount > 0 && (
                  <div className="flex justify-between col-span-2" style={{ borderColor: colors.border }}>
                    <span style={{ color: colors.textSecondary }}>Deposit (50%)</span>
                    <span style={{ color: colors.text }}>₹{depositAmount.toFixed(2)}</span>
                  </div>
                )}
                
                {outstandingAmount > 0 && selectedOrder.status?.toLowerCase() !== 'active' && (
                  <div className="flex justify-between col-span-2" style={{ borderColor: colors.border }}>
                    <span style={{ color: colors.textSecondary }}>Outstanding Amount</span>
                    <span style={{ color: '#EF4444' }}>₹{outstandingAmount.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between col-span-2 pt-2 border-t" style={{ borderColor: colors.border }}>
                  <span style={{ color: colors.textSecondary }}>Current Total</span>
                  <div className="text-right">
                    <span className="text-xl font-bold" style={{ color: colors.primary }}>
                      ₹{currentTotal.toFixed(2)}
                    </span>
                    {hasDiscount && (
                      <p className="text-xs line-through" style={{ color: colors.textSecondary }}>
                        ₹{originalTotal.toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}

            {!isSubscription && (
              <div className="flex justify-between col-span-2 pt-2 border-t" style={{ borderColor: colors.border }}>
                <span style={{ color: colors.textSecondary }}>Total Amount</span>
                <div className="text-right">
                  <span className="text-xl font-bold" style={{ color: colors.primary }}>₹{totalAmount.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Items Section */}
          {items && items.length > 0 && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: colors.text }}>
                <FaShoppingCart className="text-sm" /> Items ({items.length})
              </h4>
              <div className="space-y-3">
                {items.map((item, idx) => {
                  if (isCartOrder) {
                    const productId = parseInt(item.product_id) || 0;
                    const productDetails = getCartItemDetails(productId);
                    const quantity = parseInt(item.quantity) || 1;
                    const price = parseFloat(item.price) || 0;
                    const subtotal = parseFloat(item.subtotal) || (price * quantity);
                    const itemType = item.item_type || 'product';
                    
                    return (
                      <div 
                        key={idx} 
                        className="flex items-center gap-3 p-3 rounded-lg"
                        style={{ backgroundColor: colors.inputBackground }}
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
                          <img 
                            src={productDetails.image}
                            alt={productDetails.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(productDetails.name)}&background=FF6B35&color=fff&size=64`;
                            }}
                          />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm" style={{ color: colors.text }}>
                              {productDetails.name}
                            </span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ 
                              backgroundColor: productDetails.type === 'Veg' ? '#22C55E20' : '#EF444420',
                              color: productDetails.type === 'Veg' ? '#22C55E' : '#EF4444'
                            }}>
                              {productDetails.type === 'Veg' ? <FaLeaf className="text-[10px]" /> : <FaFish className="text-[10px]" />}
                              {productDetails.type}
                            </span>
                            {itemType && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ 
                                backgroundColor: colors.primary + '20',
                                color: colors.primary
                              }}>
                                {itemType}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs" style={{ color: colors.textSecondary }}>
                            <span>Qty: {quantity}</span>
                            <span>₹{price.toFixed(2)}</span>
                            <span className="font-medium" style={{ color: colors.primary }}>
                              Total: ₹{subtotal.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  if (isSubscription) {
                    const mealTime = item.meal_time || item.mealTime || 'Meal';
                    const mealType = item.meal_type || item.mealType || 'Veg';
                    const quantity = parseInt(item.quantity) || 1;
                    
                    return (
                      <div 
                        key={idx} 
                        className="flex items-center gap-3 p-3 rounded-lg"
                        style={{ backgroundColor: colors.inputBackground }}
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 flex items-center justify-center" style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}>
                          <div className="text-3xl relative">
                            {getMealIcon(mealTime)}
                            <span className="absolute -bottom-2 -right-2 text-[10px]">
                              {mealType === 'Veg' ? '🌿' : '🐟'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm" style={{ color: colors.text }}>
                              {mealTime}
                            </span>
                            <span className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ 
                              backgroundColor: mealType === 'Veg' ? '#22C55E20' : '#EF444420',
                              color: mealType === 'Veg' ? '#22C55E' : '#EF4444'
                            }}>
                              {mealType === 'Veg' ? <FaLeaf className="text-[10px]" /> : <FaFish className="text-[10px]" />}
                              {mealType}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-xs" style={{ color: colors.textSecondary }}>
                            <span>Qty: {quantity}</span>
                            {item.price && <span>₹{parseFloat(item.price || 0).toFixed(2)}</span>}
                            {item.food_name && (
                              <span className="text-[10px]" style={{ color: colors.textSecondary }}>
                                {item.food_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }
                  
                  return null;
                })}
              </div>
            </div>
          )}

          {isSubscription && selectedOrder.breakdown && selectedOrder.breakdown.length > 0 && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: colors.text }}>
                <FaCalendarDay className="text-sm" style={{ color: colors.primary }} />
                Meal Breakdown ({selectedOrder.breakdown.length} meals)
              </h4>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {selectedOrder.breakdown.map((meal, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center justify-between p-2 rounded-lg text-xs ${
                      meal.off ? 'opacity-60' : ''
                    }`}
                    style={{ 
                      backgroundColor: meal.off ? '#FEF3C7' : colors.inputBackground,
                      border: meal.off ? '1px solid #FCD34D' : 'none'
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <span>{getMealIcon(meal.meal_time)}</span>
                      <span style={{ color: colors.text }}>
                        {formatDate(meal.date)}
                      </span>
                      <span style={{ color: colors.textSecondary }}>
                        {meal.meal_time}
                      </span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full flex items-center gap-1" style={{ 
                        backgroundColor: meal.meal_type === 'Veg' ? '#22C55E20' : '#EF444420',
                        color: meal.meal_type === 'Veg' ? '#22C55E' : '#EF4444'
                      }}>
                        {meal.meal_type === 'Veg' ? <FaLeaf className="text-[8px]" /> : <FaFish className="text-[8px]" />}
                        {meal.meal_type || 'Veg'}
                      </span>
                      <span className="text-[10px]" style={{ color: colors.textSecondary }}>
                        {meal.food_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      {meal.quantity > 1 && (
                        <span style={{ color: colors.textSecondary }}>×{meal.quantity}</span>
                      )}
                      <span className="font-medium" style={{ color: colors.primary }}>
                        ₹{parseFloat(meal.subtotal || 0).toFixed(2)}
                      </span>
                      {meal.off && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-yellow-500 text-white">
                          Skipped
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isSubscription && mealOffs.length > 0 && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: colors.border }}>
              <h4 className="font-semibold mb-2 flex items-center gap-2" style={{ color: colors.text }}>
                <FaCalendarDay className="text-sm" style={{ color: '#F59E0B' }} />
                Skipped Meals ({mealOffs.length})
              </h4>
              <div className="space-y-2">
                {mealOffs.map((mealOff, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-2 rounded-lg"
                    style={{ 
                      backgroundColor: '#FEF3C7',
                      border: '1px solid #FCD34D'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{getMealIcon(mealOff.meal_time)}</span>
                      <div>
                        <p className="text-sm font-medium" style={{ color: '#92400E' }}>
                          {formatDateFull(mealOff.date)}
                        </p>
                        <div className="flex items-center gap-2 text-xs" style={{ color: '#92400E' }}>
                          <span>{mealOff.meal_time}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            {mealOff.meal_type?.toLowerCase() === 'veg' ? (
                              <FaLeaf className="text-green-600 text-[10px]" />
                            ) : (
                              <FaFish className="text-red-600 text-[10px]" />
                            )}
                            {mealOff.meal_type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-4 pt-4 border-t flex flex-wrap gap-3" style={{ borderColor: colors.border }}>
            {isSubscription && selectedOrder.status !== 'cancelled' && (
              <button
                className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: colors.primary + '15',
                  color: colors.primary,
                  border: `1px solid ${colors.primary}30`
                }}
                onClick={() => {
                  setShowOrderDetail(false);
                  handleOpenMealOffModal(selectedOrder);
                }}
              >
                <FaEdit className="text-sm" /> Modify Subscription
              </button>
            )}
            
            {isSubscription && selectedOrder.status !== 'cancelled' && (
              <button
                onClick={() => handleCancelSubscription(selectedOrder.id)}
                className="flex-1 py-2.5 rounded-xl font-medium text-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#EF4444' }}
              >
                <FaTrash className="text-sm" /> Cancel Subscription
              </button>
            )}

            {isCartOrder && canCancelCart && (
              <button
                onClick={() => handleCancelCartOrder(selectedOrder.order_id || selectedOrder.id)}
                className="flex-1 py-2.5 rounded-xl font-medium text-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#EF4444' }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <><FaSpinner className="animate-spin" /> Cancelling...</>
                ) : (
                  <><FaTrash className="text-sm" /> Cancel Order</>
                )}
              </button>
            )}

            {isCustomOrder && canCancelCustom && (
              <button
                onClick={() => handleCancelCustomOrder(selectedOrder.order_id || selectedOrder.id)}
                className="flex-1 py-2.5 rounded-xl font-medium text-white transition-all hover:scale-105 flex items-center justify-center gap-2"
                style={{ backgroundColor: '#EF4444' }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <><FaSpinner className="animate-spin" /> Cancelling...</>
                ) : (
                  <><FaTrash className="text-sm" /> Cancel Custom Order</>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ========== MEAL OFF MODAL COMPONENT ==========
  const MealOffModal = () => {
    if (!showMealOffModal) return null;

    const { daysInMonth, firstDay } = getDaysInMonth(currentMonth);
    const monthName = currentMonth.toLocaleString('default', { month: 'long' });
    const year = currentMonth.getFullYear();

    const days = [];
    
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-10 h-10" />);
    }

    const startDate = currentSubscription?.start_date ? new Date(currentSubscription.start_date) : null;
    const endDate = currentSubscription?.end_date ? new Date(currentSubscription.end_date) : null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const currentTotal = currentSubscription?.outstanding_amount || 
                        currentSubscription?.current_total_after_offs || 
                        currentSubscription?.projected_total || 
                        currentSubscription?.total || 0;

    const isSelectedDateFullyLocked = mealOffData.date ? getIsDateFullyLocked(mealOffData.date) : false;
    const isSelectedMealAlreadySkipped = mealOffData.date && mealOffData.meal_time && mealOffData.meal_type
      ? isMealSkipped(mealOffData.date, mealOffData.meal_time, mealOffData.meal_type)
      : false;

    const availableMealTimes = mealOffData.date ? getAvailableMealTimes(mealOffData.date) : mealTimes;

    const getDotColor = (mealTime) => {
      if (mealTime === 'Breakfast') return '#F59E0B';
      if (mealTime === 'Lunch') return '#3B82F6';
      if (mealTime === 'Dinner') return '#8B5CF6';
      return '#6B7280';
    };

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = getDateStr(currentMonth, day);
      const dateObj = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      dateObj.setHours(0, 0, 0, 0);
      
      let isInRange = true;
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        isInRange = dateObj >= start && dateObj <= end;
      }
      
      const isPastDate = dateObj < today;
      const isFullyLocked = getIsDateFullyLocked(dateStr);
      const skippedForDate = getSkippedMealsForDate(dateStr);
      const isSelectable = isInRange && !isPastDate && !isFullyLocked;
      
      const skippedMealTimes = skippedForDate.map(m => m.meal_time || m.MealTime);
      const isBreakfastSkipped = skippedMealTimes.includes('Breakfast');
      const isLunchSkipped = skippedMealTimes.includes('Lunch');
      const isDinnerSkipped = skippedMealTimes.includes('Dinner');
      
      days.push(
        <div key={day} className="relative flex flex-col items-center">
          <button
            className={`w-10 h-10 rounded-lg text-sm font-medium transition-all hover:scale-105 relative ${
              isFullyLocked ? 'opacity-100 cursor-not-allowed' : ''
            } ${!isInRange || isPastDate ? 'opacity-30 cursor-not-allowed' : ''} ${
              isSelectable ? 'hover:shadow-md cursor-pointer' : ''
            } ${mealOffData.date === dateStr && isSelectable ? 'border-2' : ''}`}
            style={{
              backgroundColor: isFullyLocked ? '#FEF3C7' : 
                             mealOffData.date === dateStr ? colors.primary : 
                             isInRange && !isPastDate ? colors.inputBackground : 'transparent',
              color: mealOffData.date === dateStr ? '#FFFFFF' : 
                     isFullyLocked ? '#92400E' :
                     isInRange && !isPastDate ? colors.text : colors.textSecondary,
              borderColor: mealOffData.date === dateStr ? colors.primary : 
                           isFullyLocked ? '#FCD34D' : colors.border,
              cursor: isSelectable ? 'pointer' : 'not-allowed',
              position: 'relative'
            }}
            onClick={() => {
              if (isSelectable) {
                setMealOffData({ ...mealOffData, date: dateStr });
              }
            }}
            disabled={!isSelectable}
          >
            <span className="relative z-10">{day}</span>
            
            <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2 flex gap-0.5 z-20">
              <span 
                className="w-1.5 h-1.5 rounded-full inline-block"
                style={{ 
                  backgroundColor: isBreakfastSkipped ? '#F59E0B' : '#D1D5DB',
                  opacity: isBreakfastSkipped ? 1 : 0.4,
                  boxShadow: isBreakfastSkipped ? '0 0 3px rgba(245,158,11,0.5)' : 'none'
                }}
                title={isBreakfastSkipped ? 'Breakfast Skipped' : 'Breakfast Available'}
              />
              <span 
                className="w-1.5 h-1.5 rounded-full inline-block"
                style={{ 
                  backgroundColor: isLunchSkipped ? '#3B82F6' : '#D1D5DB',
                  opacity: isLunchSkipped ? 1 : 0.4,
                  boxShadow: isLunchSkipped ? '0 0 3px rgba(59,130,246,0.5)' : 'none'
                }}
                title={isLunchSkipped ? 'Lunch Skipped' : 'Lunch Available'}
              />
              <span 
                className="w-1.5 h-1.5 rounded-full inline-block"
                style={{ 
                  backgroundColor: isDinnerSkipped ? '#8B5CF6' : '#D1D5DB',
                  opacity: isDinnerSkipped ? 1 : 0.4,
                  boxShadow: isDinnerSkipped ? '0 0 3px rgba(139,92,246,0.5)' : 'none'
                }}
                title={isDinnerSkipped ? 'Dinner Skipped' : 'Dinner Available'}
              />
            </div>
          </button>
          
          {isFullyLocked && (
            <span className="text-[8px] mt-0.5" style={{ color: '#92400E' }}>🔒</span>
          )}
        </div>
      );
    }

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
        <div 
          className="rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          style={{ 
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: colors.text }}>
                <FaEdit style={{ color: colors.primary }} />
                Modify Subscription
              </h3>
              <p className="text-xs" style={{ color: colors.textSecondary }}>
                Subscription #{currentSubscription?.id} - Skip Meals
              </p>
              {currentSubscription?.start_date && currentSubscription?.end_date && (
                <p className="text-xs mt-0.5" style={{ color: colors.textSecondary }}>
                  📅 {formatDate(currentSubscription.start_date)} - {formatDate(currentSubscription.end_date)}
                </p>
              )}
              <p className="text-xs font-bold mt-1" style={{ color: colors.primary }}>
                Current Total: ₹{currentTotal.toFixed(2)}
              </p>
              {mealOffs && mealOffs.length > 0 && (
                <p className="text-xs" style={{ color: '#F59E0B' }}>
                  🚫 {mealOffs.length} meal{mealOffs.length > 1 ? 's' : ''} skipped
                </p>
              )}
            </div>
            <button 
              className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{ backgroundColor: colors.inputBackground }}
              onClick={() => {
                setShowMealOffModal(false);
                setMealOffMessage(null);
                setCurrentSubscription(null);
                fetchAllOrders();
              }}
            >
              <FaTimesCircle style={{ color: colors.text }} />
            </button>
          </div>

          {mealOffMessage && (
            <div className={`p-3 rounded-xl mb-4 text-sm flex items-center gap-2 ${
              mealOffMessage.type === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 border border-green-200 dark:border-green-800' 
                : 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800'
            }`}>
              {mealOffMessage.type === 'success' ? <FaCheck /> : <FaTimes />}
              {mealOffMessage.text}
            </div>
          )}

          {/* Calendar Legend */}
          <div className="flex items-center gap-2 mb-3 text-[10px] flex-wrap" style={{ color: colors.textSecondary }}>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.primary }} />
              Selected
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }} />
              Fully Locked
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: colors.primary, opacity: 0.3 }} />
              Available
            </div>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#3B82F6' }} />
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#8B5CF6' }} />
              <span>= B/L/D</span>
            </div>
          </div>

          {/* Calendar */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-3">
              <button
                className="p-1.5 rounded-lg transition-all hover:scale-105"
                style={{ backgroundColor: colors.inputBackground }}
                onClick={() => navigateMonth(-1)}
              >
                <FaArrowLeft className="text-xs" style={{ color: colors.text }} />
              </button>
              <span className="text-sm font-medium" style={{ color: colors.text }}>
                {monthName} {year}
              </span>
              <button
                className="p-1.5 rounded-lg transition-all hover:scale-105"
                style={{ backgroundColor: colors.inputBackground }}
                onClick={() => navigateMonth(1)}
              >
                <FaArrowRight className="text-xs" style={{ color: colors.text }} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(day => (
                <div key={day} className="text-center text-[10px] font-medium" style={{ color: colors.textSecondary }}>
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {days}
            </div>
          </div>

          {/* Selected Date Info */}
          {mealOffData.date && (
            <div className="p-3 rounded-xl mb-4" style={{ 
              backgroundColor: colors.primary + '10',
              border: `1px solid ${colors.primary}25`
            }}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium" style={{ color: colors.text }}>
                  📅 {formatDateFull(mealOffData.date)}
                </span>
                {isSelectedDateFullyLocked && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500 text-white">
                    🔒 Fully Locked
                  </span>
                )}
                {!isSelectedDateFullyLocked && (
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ 
                    backgroundColor: colors.primary + '20',
                    color: colors.primary
                  }}>
                    {3 - getSkippedMealsForDate(mealOffData.date).length} meals available
                  </span>
                )}
              </div>
              
              {getSkippedMealsForDate(mealOffData.date).length > 0 && (
                <div className="mt-1 text-xs" style={{ color: colors.textSecondary }}>
                  <span className="font-medium">Skipped:</span>{' '}
                  {getSkippedMealsForDate(mealOffData.date).map((m, idx) => (
                    <span key={idx}>
                      {idx > 0 && ', '}
                      <span 
                        className="inline-flex items-center gap-1"
                        style={{ 
                          color: m.meal_time === 'Breakfast' ? '#F59E0B' : 
                                 m.meal_time === 'Lunch' ? '#3B82F6' : '#8B5CF6'
                        }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: getDotColor(m.meal_time) }} />
                        {m.meal_time}
                      </span>
                      <span style={{ color: colors.textSecondary }}> ({m.meal_type})</span>
                    </span>
                  ))}
                </div>
              )}
              
              {!isSelectedDateFullyLocked && (
                <div className="mt-1 text-xs" style={{ color: colors.textSecondary }}>
                  <span className="font-medium">Available:</span>{' '}
                  {getAvailableMealTimes(mealOffData.date).map((time, idx) => (
                    <span key={idx}>
                      {idx > 0 && ', '}
                      <span style={{ 
                        color: time === 'Breakfast' ? '#F59E0B' : 
                               time === 'Lunch' ? '#3B82F6' : '#8B5CF6'
                      }}>
                        {time}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Meal Time Selection */}
          <div className="mb-4">
            <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
              Select Meal Time to Skip
            </label>
            <div className="grid grid-cols-3 gap-2">
              {mealTimes.map((time) => {
                const isSkipped = mealOffData.date ? isMealSkipped(mealOffData.date, time, mealOffData.meal_type) : false;
                const isAvailable = mealOffData.date ? getAvailableMealTimes(mealOffData.date).includes(time) : true;
                const isDisabled = !mealOffData.date || isSelectedDateFullyLocked || !isAvailable || isSkipped;
                
                const timeColor = time === 'Breakfast' ? '#F59E0B' : 
                                 time === 'Lunch' ? '#3B82F6' : '#8B5CF6';
                
                return (
                  <button
                    key={time}
                    className={`py-2 px-3 rounded-xl text-xs font-medium transition-all hover:scale-105 ${
                      mealOffData.meal_time === time ? 'border-2' : 'border'
                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{
                      borderColor: mealOffData.meal_time === time ? colors.primary : colors.border,
                      backgroundColor: isSkipped ? '#FEF3C7' :
                                     isAvailable ? (mealOffData.meal_time === time ? colors.primary + '20' : colors.inputBackground) :
                                     colors.inputBackground,
                      color: isSkipped ? '#92400E' :
                             isAvailable ? (mealOffData.meal_time === time ? colors.primary : colors.text) :
                             colors.textSecondary
                    }}
                    onClick={() => {
                      if (!isDisabled && isAvailable) {
                        setMealOffData({ ...mealOffData, meal_time: time });
                      }
                    }}
                    disabled={isDisabled}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span 
                        className="w-1.5 h-1.5 rounded-full inline-block"
                        style={{ 
                          backgroundColor: isSkipped ? timeColor : '#D1D5DB',
                          opacity: isSkipped ? 1 : 0.4
                        }}
                      />
                      <span className="text-base">{getMealIcon(time)}</span>
                    </div>
                    {time}
                    {isSkipped && <span className="text-[8px] block text-yellow-600">✓ Skipped</span>}
                    {!isAvailable && !isSkipped && <span className="text-[8px] block text-gray-400">Locked</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Meal Type Selection */}
          <div className="mb-4">
            <label className="text-sm font-medium block mb-1.5" style={{ color: colors.textSecondary }}>
              Select Meal Type
            </label>
            <div className="grid grid-cols-2 gap-2">
              {mealTypes.map((type) => {
                const isSkipped = mealOffData.date && mealOffData.meal_time ? 
                  isMealSkipped(mealOffData.date, mealOffData.meal_time, type) : false;
                const isDisabled = !mealOffData.date || !mealOffData.meal_time || isSelectedDateFullyLocked || isSkipped;
                
                return (
                  <button
                    key={type}
                    className={`py-2 px-3 rounded-xl text-xs font-medium transition-all hover:scale-105 flex items-center justify-center gap-2 ${
                      mealOffData.meal_type === type ? 'border-2' : 'border'
                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{
                      borderColor: mealOffData.meal_type === type ? colors.primary : colors.border,
                      backgroundColor: isSkipped ? '#FEF3C7' :
                                     mealOffData.meal_type === type ? colors.primary + '20' : colors.inputBackground,
                      color: isSkipped ? '#92400E' :
                             mealOffData.meal_type === type ? colors.primary : colors.text
                    }}
                    onClick={() => {
                      if (!isDisabled) {
                        setMealOffData({ ...mealOffData, meal_type: type });
                      }
                    }}
                    disabled={isDisabled}
                  >
                    {type === 'Veg' ? (
                      <FaLeaf className={mealOffData.meal_type === type ? 'text-green-500' : 'text-gray-400'} />
                    ) : (
                      <FaFish className={mealOffData.meal_type === type ? 'text-red-500' : 'text-gray-400'} />
                    )}
                    {type}
                    {isSkipped && <span className="text-[8px] text-yellow-600">✓ Skipped</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Existing Meal Offs List */}
          {mealOffs && mealOffs.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium flex items-center gap-2" style={{ color: colors.text }}>
                  <FaList className="text-xs" />
                  Skipped Meals ({mealOffs.length})
                </h4>
                <button
                  className="text-xs hover:underline"
                  style={{ color: colors.primary }}
                  onClick={() => setShowMealOffList(!showMealOffList)}
                >
                  {showMealOffList ? 'Hide' : 'Show'}
                </button>
              </div>
              {showMealOffList && (
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {mealOffs.map((mealOff, idx) => {
                    const dotColor = mealOff.meal_time === 'Breakfast' ? '#F59E0B' : 
                                     mealOff.meal_time === 'Lunch' ? '#3B82F6' : '#8B5CF6';
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-2 rounded-lg"
                        style={{ 
                          backgroundColor: '#FEF3C7',
                          border: '1px solid #FCD34D'
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <span 
                            className="w-2 h-2 rounded-full inline-block"
                            style={{ backgroundColor: dotColor }}
                          />
                          <div>
                            <p className="text-xs font-medium" style={{ color: '#92400E' }}>
                              {formatDateFull(mealOff.date)}
                            </p>
                            <div className="flex items-center gap-1 text-[10px]" style={{ color: '#92400E' }}>
                              <span>{mealOff.meal_time}</span>
                              <span>•</span>
                              <span className="flex items-center gap-0.5">
                                {mealOff.meal_type?.toLowerCase() === 'veg' ? (
                                  <FaLeaf className="text-green-600 text-[8px]" />
                                ) : (
                                  <FaFish className="text-red-600 text-[8px]" />
                                )}
                                {mealOff.meal_type}
                              </span>
                            </div>
                          </div>
                        </div>
                        <button
                          className="p-1 rounded-full transition-all hover:scale-110"
                          style={{ color: '#EF4444' }}
                          onClick={() => handleRemoveMealOff(mealOff.date, mealOff.meal_time, mealOff.meal_type)}
                        >
                          <FaTrash className="text-xs" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Selected Summary */}
          {mealOffData.date && mealOffData.meal_time && mealOffData.meal_type && !isSelectedDateFullyLocked && !isSelectedMealAlreadySkipped && (
            <div className="p-3 rounded-xl mb-4" style={{ 
              backgroundColor: colors.primary + '10',
              border: `1px solid ${colors.primary}25`
            }}>
              <p className="text-xs font-medium" style={{ color: colors.text }}>
                <FaInfoCircle className="inline mr-1.5" />
                You're about to skip:
              </p>
              <div className="flex items-center gap-2 mt-1 text-sm" style={{ color: colors.primary }}>
                <span>{formatDateFull(mealOffData.date)}</span>
                <span>•</span>
                <span>{getMealIcon(mealOffData.meal_time)} {mealOffData.meal_time}</span>
                <span>•</span>
                <span className="flex items-center gap-1">
                  {mealOffData.meal_type === 'Veg' ? (
                    <FaLeaf className="text-green-500" />
                  ) : (
                    <FaFish className="text-red-500" />
                  )}
                  {mealOffData.meal_type}
                </span>
              </div>
              <div className="mt-1 text-xs" style={{ color: colors.textSecondary }}>
                {getSkippedMealsForDate(mealOffData.date).length + 1} of 3 meals will be skipped on this date
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              className="flex-1 py-2.5 rounded-xl text-sm font-medium transition-all hover:scale-[1.02]"
              style={{ 
                backgroundColor: colors.inputBackground,
                color: colors.text,
                border: `1px solid ${colors.border}`
              }}
              onClick={() => {
                setShowMealOffModal(false);
                setMealOffMessage(null);
                setCurrentSubscription(null);
                fetchAllOrders();
              }}
            >
              Close
            </button>
            <button
              className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
              style={{ 
                backgroundColor: mealOffData.date && mealOffData.meal_time && mealOffData.meal_type && !isSelectedDateFullyLocked && !isSelectedMealAlreadySkipped ? colors.primary : colors.border,
                opacity: mealOffData.date && mealOffData.meal_time && mealOffData.meal_type && !isSelectedDateFullyLocked && !isSelectedMealAlreadySkipped ? 1 : 0.5,
                cursor: mealOffData.date && mealOffData.meal_time && mealOffData.meal_type && !isSelectedDateFullyLocked && !isSelectedMealAlreadySkipped ? 'pointer' : 'not-allowed'
              }}
              onClick={handleMarkMealOff}
              disabled={!mealOffData.date || !mealOffData.meal_time || !mealOffData.meal_type || isSelectedDateFullyLocked || isSelectedMealAlreadySkipped || mealOffLoading}
            >
              {mealOffLoading ? (
                <><FaSpinner className="animate-spin" /> Skipping...</>
              ) : isSelectedDateFullyLocked ? (
                <><FaLock /> Date Fully Locked</>
              ) : isSelectedMealAlreadySkipped ? (
                <><FaCheck /> Already Skipped</>
              ) : (
                <><FaPlus /> Skip Meal</>
              )}
            </button>
          </div>

          <p className="text-[10px] text-center mt-4" style={{ color: colors.textSecondary + '80' }}>
            <FaInfoCircle className="inline mr-1" />
            Skip meals one by one. Date locks only when all 3 meals are skipped.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen pb-20" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="relative pt-6 pb-4 px-4 border-b"
           style={{
             background: `linear-gradient(180deg, ${colors.primary}20 0%, ${colors.background} 100%)`,
             borderColor: colors.border
           }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: colors.text }}>
              <FaHistory style={{ color: colors.primary }} />
              My Orders
            </h1>
            <p className="text-sm" style={{ color: colors.textSecondary }}>
              <span style={{ color: colors.textSecondary }}>📱 {user?.phone || '6295028775'}</span>
            </p>
          </div>
          <button
            onClick={() => navigate('/profile')}
            className="px-4 py-1.5 rounded-full text-sm font-medium transition-all hover:scale-105"
            style={{ 
              backgroundColor: colors.inputBackground,
              color: colors.text,
              border: `1px solid ${colors.border}`
            }}
          >
            Back to Profile
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-4 max-w-4xl">
        {showOrderDetail ? (
          <OrderDetailView />
        ) : (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-6 bg-gray-100 dark:bg-gray-800 rounded-xl p-1 overflow-x-auto">
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'all' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'all' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'all' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('all')}
              >
                All Orders
              </button>
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'cart' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'cart' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'cart' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('cart')}
              >
                <FaShoppingCart className="inline mr-1.5" /> Cart Orders
              </button>
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'subscription' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'subscription' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'subscription' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('subscription')}
              >
                <FaCalendarAlt className="inline mr-1.5" /> Monthly
              </button>
              <button
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'custom' ? 'bg-white dark:bg-gray-700 shadow-sm' : 'bg-transparent'
                }`}
                style={{ 
                  color: activeTab === 'custom' ? colors.primary : colors.textSecondary,
                  boxShadow: activeTab === 'custom' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
                }}
                onClick={() => setActiveTab('custom')}
              >
                <FaClock className="inline mr-1.5" /> Custom
              </button>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-4 p-3 rounded-lg text-sm flex items-start gap-2"
                   style={{ 
                     backgroundColor: '#FEF2F2',
                     color: '#DC2626',
                     border: '1px solid #FECACA'
                   }}>
                <span className="mt-0.5">⚠️</span>
                <span>{error}</span>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-xs font-medium hover:underline"
                  style={{ color: '#DC2626' }}
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Orders List */}
            {isLoading ? (
              <div className="text-center py-12">
                <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: colors.primary }} />
                <p className="mt-4" style={{ color: colors.textSecondary }}>Loading orders...</p>
              </div>
            ) : filteredOrders.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📭</div>
                <h3 className="text-xl font-bold mb-2" style={{ color: colors.text }}>No Orders Yet</h3>
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  {activeTab === 'all' 
                    ? "You haven't placed any orders yet." 
                    : activeTab === 'cart'
                    ? "You don't have any cart orders."
                    : activeTab === 'subscription' 
                    ? "You don't have any monthly subscriptions." 
                    : "You don't have any custom date orders."}
                </p>
                <button
                  onClick={() => navigate('/cart-service')}
                  className="mt-4 px-6 py-2 rounded-full text-white font-medium transition-all hover:scale-105"
                  style={{ backgroundColor: colors.primary }}
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredOrders.map((order, index) => {
                  const orderItems = order.items || [];
                  const isCartOrder = order.orderType === 'cart';
                  const isSubscription = order.orderType === 'subscription';
                  const isCustomOrder = order.orderType === 'custom';
                  const mealOffs = order.mealOffs || [];
                  const totalAmount = parseFloat(order.total || 0);
                  const originalTotal = parseFloat(order.projected_total || 0);
                  const depositAmount = parseFloat(order.deposit_amount || 0);
                  const totalMeals = order.total_meals || 0;
                  const outstandingAmount = parseFloat(order.outstanding_amount || 0);
                  const discountPercent = order.discount_percent || 0;
                  
                  const canCancelCart = isCartOrder && 
                    (order.status?.toLowerCase() === 'pending' || 
                     order.status?.toLowerCase() === 'placed' ||
                     order.status?.toLowerCase() === 'confirmed') &&
                    order.status?.toLowerCase() !== 'cancelled';
                  
                  const canCancelCustom = isCustomOrder && 
                    (order.status?.toLowerCase() === 'pending' || 
                     order.status?.toLowerCase() === 'placed') &&
                    order.status?.toLowerCase() !== 'cancelled';
                  
                  const displayTotal = isSubscription && outstandingAmount > 0 
                    ? outstandingAmount 
                    : totalAmount;
                  
                  const hasDiscount = isSubscription && discountPercent > 0;
                  
                  return (
                    <div
                      key={index}
                      className="rounded-xl p-4 cursor-pointer transition-all hover:shadow-md"
                      style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
                      onClick={() => handleViewOrder(order, order.orderType)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-lg">
                              {isCartOrder ? '🛒' : isSubscription ? '📆' : '📅'}
                            </span>
                            <p className="font-medium" style={{ color: colors.text }}>
                              {isCartOrder 
                                ? `Order #${order.order_id}` 
                                : isSubscription 
                                  ? `Monthly Subscription #${order.id}` 
                                  : `Custom Order #${order.order_id || order.id}`}
                            </p>
                            {isCartOrder && order.payment && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ 
                                backgroundColor: getPaymentColor(order.payment) + '20',
                                color: getPaymentColor(order.payment)
                              }}>
                                {order.payment}
                              </span>
                            )}
                            {isSubscription && hasDiscount && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-green-500 text-white">
                                {discountPercent}% Off
                              </span>
                            )}
                          </div>
                          
                          {isSubscription && (
                            <div className="mt-1">
                              <p className="text-xs" style={{ color: colors.textSecondary }}>
                                📅 {formatDate(order.start_date)} - {formatDate(order.end_date)}
                              </p>
                              <div className="flex items-center gap-3 text-xs flex-wrap" style={{ color: colors.textSecondary }}>
                                <span>🍽️ {totalMeals} meal{totalMeals !== 1 ? 's' : ''}</span>
                                {depositAmount > 0 && (
                                  <span>💰 Deposit: ₹{depositAmount.toFixed(2)}</span>
                                )}
                                {outstandingAmount > 0 && order.status?.toLowerCase() !== 'active' && (
                                  <span style={{ color: '#EF4444' }}>Remaining: ₹{outstandingAmount.toFixed(2)}</span>
                                )}
                                {order.delivery_address && order.delivery_address !== 'N/A' && (
                                  <span className="flex items-center gap-1">
                                    <FaMapMarkerAlt className="text-[10px]" /> {order.delivery_address}
                                  </span>
                                )}
                              </div>
                              {order.meals_by_type && Object.keys(order.meals_by_type).length > 0 && (
                                <div className="flex items-center gap-2 text-xs flex-wrap mt-1">
                                  <span style={{ color: colors.textSecondary }}>Meals:</span>
                                  {Object.entries(order.meals_by_type).map(([key, count], idx) => {
                                    const [mealTime, mealType] = key.split(' (');
                                    const type = mealType ? mealType.replace(')', '') : 'Veg';
                                    return (
                                      <span key={idx} className="px-2 py-0.5 rounded-full flex items-center gap-1" style={{ 
                                        backgroundColor: type === 'Veg' ? '#22C55E20' : '#EF444420',
                                        color: type === 'Veg' ? '#22C55E' : '#EF4444'
                                      }}>
                                        {getMealIcon(mealTime)}
                                        {mealTime} ×{count}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                          
                          <p className="text-xs" style={{ color: colors.textSecondary }}>
                            {orderItems.length} item{orderItems.length > 1 ? 's' : ''}
                          </p>
                          {isSubscription && mealOffs.length > 0 && (
                            <p className="text-xs flex items-center gap-1" style={{ color: '#F59E0B' }}>
                              <FaCalendarDay className="text-[10px]" />
                              {mealOffs.length} skipped meal{mealOffs.length > 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                        <div className="text-right ml-4 flex-shrink-0">
                          <p className="font-bold" style={{ color: colors.primary }}>
                            ₹{displayTotal.toFixed(2)}
                          </p>
                          {isSubscription && originalTotal > 0 && (
                            <p className="text-xs line-through" style={{ color: colors.textSecondary }}>
                              ₹{originalTotal.toFixed(2)}
                            </p>
                          )}
                          <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: getStatusColor(order.status) }}>
                            {getStatusText(order.status)}
                          </span>
                          {isSubscription && order.status !== 'cancelled' && (
                            <button
                              className="mt-1 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-full justify-center"
                              style={{ 
                                backgroundColor: colors.primary + '20',
                                color: colors.primary
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenMealOffModal(order);
                              }}
                            >
                              <FaEdit className="text-[10px]" /> Modify
                            </button>
                          )}
                          {canCancelCart && (
                            <button
                              className="mt-1 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-full justify-center"
                              style={{ 
                                backgroundColor: '#EF444420',
                                color: '#EF4444',
                                border: '1px solid #EF444430'
                              }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (window.confirm('Are you sure you want to cancel this order?')) {
                                  try {
                                    await handleCancelCartOrder(order.order_id);
                                  } catch (error) {
                                    console.error('Failed to cancel order:', error);
                                  }
                                }
                              }}
                            >
                              <FaTrash className="text-[10px]" /> Cancel
                            </button>
                          )}
                          {canCancelCustom && (
                            <button
                              className="mt-1 text-xs px-2 py-0.5 rounded-full flex items-center gap-1 w-full justify-center"
                              style={{ 
                                backgroundColor: '#EF444420',
                                color: '#EF4444',
                                border: '1px solid #EF444430'
                              }}
                              onClick={async (e) => {
                                e.stopPropagation();
                                await handleCancelCustomOrder(order.order_id || order.id);
                              }}
                            >
                              <FaTrash className="text-[10px]" /> Cancel
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {orderItems.length > 0 && (
                        <div className="mt-2 pt-2 border-t flex gap-2 flex-wrap" style={{ borderColor: colors.border }}>
                          {orderItems.slice(0, 4).map((item, idx) => {
                            if (isSubscription) {
                              const mealTime = item.meal_time || item.mealTime || 'Meal';
                              const mealType = item.meal_type || item.mealType || 'Veg';
                              const quantity = parseInt(item.quantity) || 1;
                              
                              return (
                                <div 
                                  key={idx} 
                                  className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full"
                                  style={{ backgroundColor: colors.inputBackground, color: colors.textSecondary }}
                                >
                                  <span>{getMealIcon(mealTime)}</span>
                                  <span>{mealTime}</span>
                                  <span className="text-[10px] flex items-center gap-0.5" style={{ 
                                    color: mealType === 'Veg' ? '#22C55E' : '#EF4444'
                                  }}>
                                    {mealType === 'Veg' ? <FaLeaf className="text-[8px]" /> : <FaFish className="text-[8px]" />}
                                    {mealType}
                                  </span>
                                  {quantity > 1 && <span>×{quantity}</span>}
                                </div>
                              );
                            } else {
                              const productId = parseInt(item.product_id);
                              const productImage = getProductImage(productId);
                              const productName = getProductName(productId);
                              
                              return (
                                <div 
                                  key={idx} 
                                  className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-full"
                                  style={{ backgroundColor: colors.inputBackground, color: colors.textSecondary }}
                                >
                                  <img 
                                    src={productImage}
                                    alt={productName}
                                    className="w-5 h-5 rounded-full object-cover"
                                    onError={(e) => {
                                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(productName)}&background=FF6B35&color=fff&size=32`;
                                    }}
                                  />
                                  <span>{productName}</span>
                                  {item.quantity && <span>×{item.quantity}</span>}
                                </div>
                              );
                            }
                          })}
                          {orderItems.length > 4 && (
                            <span className="text-xs px-2 py-1" style={{ color: colors.textSecondary }}>
                              +{orderItems.length - 4} more
                            </span>
                          )}
                        </div>
                      )}
                      
                      <div className="mt-2 flex justify-end">
                        <button
                          className="text-xs flex items-center gap-1 px-3 py-1 rounded-lg transition-all hover:scale-105"
                          style={{ 
                            backgroundColor: colors.primary + '20',
                            color: colors.primary
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOrder(order, order.orderType);
                          }}
                        >
                          <FaEye className="text-xs" /> View Details
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Meal Off Modal - Using key to force re-render */}
      <MealOffModal key={modalKey} />
    </div>
  );
};

export default MyOrders;